import { Controller } from "@hotwired/stimulus"

const DESKTOP_BREAKPOINT = "(min-width: 1024px)"
const TABLET_BREAKPOINT = "(min-width: 768px) and (max-width: 1023px)"
const LEGACY_DESKTOP_BREAKPOINT = "(min-width: 768px)"

// Keeps only the active breakpoint representation in the accessibility tree
// and clears focus when the focused node moves into a newly inert region.
export default class extends Controller {
  static targets = ["desktop", "mobile", "tablet", "desktopSort", "mobileSort"]

  connect() {
    this.mediaQueries = [
      window.matchMedia?.(DESKTOP_BREAKPOINT),
      window.matchMedia?.(TABLET_BREAKPOINT),
      window.matchMedia?.(LEGACY_DESKTOP_BREAKPOINT)
    ].filter(Boolean)
    this.boundSync = this.sync.bind(this)

    this.mediaQueries.forEach((mediaQuery) => {
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", this.boundSync)
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(this.boundSync)
      }
    })

    this.sync()
  }

  disconnect() {
    this.mediaQueries?.forEach((mediaQuery) => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", this.boundSync)
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(this.boundSync)
      }
    })
  }

  desktopTargetConnected() {
    this.sync()
  }

  mobileTargetConnected() {
    this.sync()
  }

  tabletTargetConnected() {
    this.sync()
  }

  sync() {
    if (this.hasTabletTarget) {
      this.syncThreeBreakpoints()
    } else {
      this.syncLegacyBreakpoints()
    }
  }

  syncThreeBreakpoints() {
    const isDesktop = window.matchMedia(DESKTOP_BREAKPOINT).matches
    const isTablet = window.matchMedia(TABLET_BREAKPOINT).matches
    const isMobile = !isDesktop && !isTablet

    if (this.hasDesktopTarget) {
      this.setTargetInert(this.desktopTarget, !isDesktop)
    }
    if (this.hasTabletTarget) {
      this.setTargetInert(this.tabletTarget, !isTablet)
    }
    if (this.hasMobileTarget) {
      this.setTargetInert(this.mobileTarget, !isMobile)
    }

    if (!isDesktop && this.hasDesktopTarget) this.blurIfInsideInert(this.desktopTarget)
    if (!isTablet && this.hasTabletTarget) this.blurIfInsideInert(this.tabletTarget)
    if (!isMobile && this.hasMobileTarget) this.blurIfInsideInert(this.mobileTarget)

    this.syncSortFields(isMobile)
  }

  syncLegacyBreakpoints() {
    const isDesktop = window.matchMedia(LEGACY_DESKTOP_BREAKPOINT).matches

    if (this.hasDesktopTarget) {
      this.setTargetInert(this.desktopTarget, !isDesktop)
    }
    if (this.hasMobileTarget) {
      this.setTargetInert(this.mobileTarget, isDesktop)
    }

    this.blurIfInsideInert(
      isDesktop
        ? (this.hasMobileTarget ? this.mobileTarget : null)
        : (this.hasDesktopTarget ? this.desktopTarget : null)
    )

    this.syncSortFields(!isDesktop)
  }

  syncSortFields(isMobileActive) {
    if (this.hasDesktopSortTarget) {
      this.desktopSortTarget.disabled = isMobileActive
    }
    if (this.hasMobileSortTarget) {
      this.mobileSortTarget.disabled = !isMobileActive
    }
  }

  setTargetInert(target, inert) {
    if (!target) return

    if (inert) {
      target.setAttribute("inert", "")
    } else {
      target.removeAttribute("inert")
    }
  }

  blurIfInsideInert(root) {
    if (!root) return

    const active = document.activeElement
    if (active && root.contains(active) && typeof active.blur === "function") {
      active.blur()
    }
  }
}
